import React from 'react'
import Modal from '../../molecules/Modal'
import { Divider, Typography } from '@material-ui/core'
import { DateTime } from 'luxon'

const ReleaseNotes = ({ children, beforeShow }: { children: React.ReactNode, beforeShow?: () => void }) => {
  const [show, setShow] = React.useState(false)

  const openDialog = () => setShow(true)
  const closeDialog = () => setShow(false)

  const clonedTrigger = React.cloneElement(children as React.ReactElement, {
    onClick: () => {
      if (beforeShow) {
        beforeShow()
      }
      openDialog()
    },
    style: { cursor: 'pointer' },
  })

  return (
    <>
      {clonedTrigger}
      <Modal open={show} onClose={closeDialog} heading={'Release notes'}>
        <ReleaseNotesEntry date={'04-21-2026'} versionNumber={'1.5.22'}>
          <ReleaseNotesForApp title={'Pilot'} spacing>
            <ul>
              <li>Added new vessel types: Offshore Support Vessel (OSV), RoRo, Heavy Lift Vessel, Woodchip Carrier.</li>
            </ul>
          </ReleaseNotesForApp>
        </ReleaseNotesEntry>
        <ReleaseNotesEntry date={'11-24-2025'} versionNumber={'1.5.21'}>
          <ReleaseNotesForApp title={'Pilot'} spacing>
            <ul>
              <li>Fixed an issue where reordering stages caused tug positions to reset.</li>
            </ul>
          </ReleaseNotesForApp>
        </ReleaseNotesEntry>
        <ReleaseNotesEntry date={'11-03-2025'} versionNumber={'1.5.20'}>
          <ReleaseNotesForApp title={'Pilot'} spacing>
            <ul>
              <li>Added a loading indicator for Tugs.</li>
              <li>Added a loading indicator when clearing or recording a Timestamp.</li>
              <li>Added an option to add multi-stage tug configurations.</li>
              <li>Introduced six additional tug position options to expand
                operational flexibility.</li>
            </ul>
          </ReleaseNotesForApp>
        </ReleaseNotesEntry>
        <ReleaseNotesEntry date={'06-16-2025'} versionNumber={'1.5.19'}>
          <ReleaseNotesForApp title="Pilot" spacing>
            <ul>
              <li>
                The list of Primary and Secondary pilots now only displays users
                relevant to the currently selected port.
              </li>
            </ul>
          </ReleaseNotesForApp>
        </ReleaseNotesEntry>
        <ReleaseNotesEntry date={'03-25-2025'} versionNumber={'1.5.18'}>
          <ReleaseNotesForApp title="Pilot">
            <ul>
              <li>
                Several input fields have been updated to enhance usability and
                responsiveness on tablet devices.
              </li>
              <li>
                Resolved layout issues affecting the Max Draft field, ensuring
                proper display and alignment on smaller screens.
              </li>
            </ul>
          </ReleaseNotesForApp>
        </ReleaseNotesEntry>
        <ReleaseNotesEntry date={'12-10-2024'} versionNumber={'1.5.17'}>
          <ReleaseNotesForApp title="Pilot">
            <ul>
              <li>
                Added support for additional transfer methods: Tug, Wharf, and
                Car.
              </li>
              <li>
                Introduced new fields to capture EEXI (Energy Efficiency
                Existing Ship Index) data in the vessel details.
              </li>
              <li>
                Made minor layout improvements to the Vessel Details screen for
                better readability and consistency.
              </li>
            </ul>
          </ReleaseNotesForApp>
        </ReleaseNotesEntry>
        <ReleaseNotesEntry date={'07-12-2024'} versionNumber={'1.5.16'}>
          <ReleaseNotesForApp title="Pilot">
            <ul>
              <li>
                The user interface for selecting tugs has been updated for a
                more intuitive and streamlined experience.
              </li>
              <li>
                Introduced two additional tug position options to expand
                operational flexibility.
              </li>
              <li>
                Users can now re-authenticate after a session expires,
                preventing lockouts and ensuring smoother continuity of use.
              </li>
            </ul>
          </ReleaseNotesForApp>
        </ReleaseNotesEntry>
      </Modal>
    </>
  )
}

const ReleaseNotesEntry = ({
  date,
  versionNumber,
  children,
}: {
  date: string
  versionNumber: string
  children: React.ReactNode
}) => {
  return (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignContent: 'baseline',
          marginTop: '16px',
        }}
      >
        <Typography variant="h3">Version {versionNumber}</Typography>
        <Typography variant="h3">
          {DateTime.fromFormat(date, 'MM-dd-yyyy').toFormat('DD')}
        </Typography>
      </div>
      <div style={{ marginLeft: '20px', marginTop: '8px' }}>{children}</div>
      <Divider />
    </>
  )
}

const ReleaseNotesForApp = ({
  children,
  title,
  spacing,
}: {
  children: React.ReactNode
  title: 'Pilot' | 'Admin'
  spacing?: boolean
}) => {
  return (
    <>
      <Typography style={{ marginBottom: '8px' }}>{title} app</Typography>
      <div style={{ marginLeft: '24px' }}>{children}</div>
      {spacing && <br />}
    </>
  )
}

export default React.memo(ReleaseNotes)
